/**
 * BLOQUEOS OPERATIVOS DE UN CENTRO — FASE 6.3 §J
 *
 * CRUD simple sobre `bloqueos_centro_ubicacion` (FASE 6.1 §G): en 6.1 son
 * sólo lectura/alerta para la proyección, nunca festivo. Alcance
 * reducido a propósito para no hacer crecer 6.3: el formulario sólo crea
 * bloqueos de CENTRO COMPLETO (sin selector de ubicación concreta) — el
 * modelo ya admite `idUbicacion`, pero elegir una ubicación necesitaría
 * el mismo árbol que ya tiene la pestaña «Ubicaciones»; se deja fuera y
 * se reporta como candidato de una fase posterior en vez de forzarlo aquí.
 */
import { useState, type FormEvent, type ReactNode } from 'react';

import { api, FalloApi, type Bloqueo } from '../api.ts';
import { Error as ErrorCaja, Panel, fecha, useDatos, Vacio } from './base.tsx';

export function BloqueosCentro({ idCentro, puedeGestionar }: { idCentro: string; puedeGestionar: boolean }): ReactNode {
  const bloqueos = useDatos(() => api.get<{ bloqueos: Bloqueo[] }>(`/api/centros/${idCentro}/bloqueos`), [idCentro]);
  const [creando, setCreando] = useState(false);

  return (
    <div className="panel" style={{ marginTop: 16 }}>
      <div className="panel-cabecera">
        <h2>Bloqueos operativos</h2>
        {puedeGestionar && (
          <button type="button" className="pequeno" onClick={() => setCreando((v) => !v)}>
            {creando ? 'Cancelar' : 'Nuevo bloqueo'}
          </button>
        )}
      </div>

      {creando && (
        <FormNuevoBloqueo
          idCentro={idCentro}
          alCrear={() => {
            setCreando(false);
            bloqueos.recargar();
          }}
          alCancelar={() => setCreando(false)}
        />
      )}

      <div className="panel-cuerpo sin-relleno">
        <Panel estado={bloqueos}>
          {(b) =>
            b.bloqueos.length === 0 ? (
              <Vacio titulo="Sin bloqueos operativos">
                <p className="suave">No cortan la generación automática de OT — son aviso/alerta para quien planifica.</p>
              </Vacio>
            ) : (
              <div className="tabla-desliza">
                <table>
                  <thead>
                    <tr>
                      <th>Desde</th>
                      <th>Hasta</th>
                      <th>Horario</th>
                      <th>Ámbito</th>
                      <th>Motivo</th>
                      {puedeGestionar && <th />}
                    </tr>
                  </thead>
                  <tbody>
                    {b.bloqueos.map((bl) => (
                      <FilaBloqueo key={bl.id} idCentro={idCentro} bloqueo={bl} puedeGestionar={puedeGestionar} onBorrado={bloqueos.recargar} />
                    ))}
                  </tbody>
                </table>
              </div>
            )
          }
        </Panel>
      </div>
    </div>
  );
}

function FilaBloqueo({
  idCentro,
  bloqueo,
  puedeGestionar,
  onBorrado,
}: {
  idCentro: string;
  bloqueo: Bloqueo;
  puedeGestionar: boolean;
  onBorrado: () => void;
}): ReactNode {
  const [confirmando, setConfirmando] = useState(false);
  const [borrando, setBorrando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function borrar(): Promise<void> {
    setBorrando(true);
    setError(null);
    try {
      await api.del(`/api/centros/${idCentro}/bloqueos/${bloqueo.id}`);
      onBorrado();
    } catch (err) {
      setError(err);
      setBorrando(false);
    }
  }

  return (
    <tr>
      <td className="mono">{fecha(bloqueo.fechaDesde)}</td>
      <td className="mono">{fecha(bloqueo.fechaHasta)}</td>
      <td>{bloqueo.horaInicio !== null && bloqueo.horaFin !== null ? `${bloqueo.horaInicio}–${bloqueo.horaFin}` : 'Todo el día'}</td>
      <td>{bloqueo.idUbicacion === null ? 'Centro completo' : 'Ubicación concreta'}</td>
      <td>{bloqueo.motivo}</td>
      {puedeGestionar && (
        <td className="derecha">
          {error !== null ? (
            <span className="pequena tenue">Error al eliminar.</span>
          ) : confirmando ? (
            <span className="fila">
              <button type="button" className="pequeno" onClick={() => { void borrar(); }} disabled={borrando}>
                {borrando ? '…' : 'Confirmar'}
              </button>
              <button type="button" className="pequeno" onClick={() => setConfirmando(false)} disabled={borrando}>
                Cancelar
              </button>
            </span>
          ) : (
            <button type="button" className="pequeno" onClick={() => setConfirmando(true)}>
              Eliminar
            </button>
          )}
        </td>
      )}
    </tr>
  );
}

function FormNuevoBloqueo({ idCentro, alCrear, alCancelar }: { idCentro: string; alCrear: () => void; alCancelar: () => void }): ReactNode {
  const hoy = new Date().toISOString().slice(0, 10);
  const [fechaDesde, setFechaDesde] = useState(hoy);
  const [fechaHasta, setFechaHasta] = useState(hoy);
  const [conHorario, setConHorario] = useState(false);
  const [horaInicio, setHoraInicio] = useState('08:00');
  const [horaFin, setHoraFin] = useState('17:00');
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [camposMal, setCamposMal] = useState<Record<string, string>>({});

  async function enviar(e: FormEvent): Promise<void> {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    setCamposMal({});
    try {
      await api.post(`/api/centros/${idCentro}/bloqueos`, {
        fechaDesde,
        fechaHasta,
        horaInicio: conHorario ? horaInicio : null,
        horaFin: conHorario ? horaFin : null,
        motivo: motivo.trim(),
      });
      alCrear();
    } catch (err) {
      if (err instanceof FalloApi && err.erroresDeCampo != null) setCamposMal(err.erroresDeCampo);
      else setError(err);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <form className="form-inline" onSubmit={(e) => { void enviar(e); }}>
      <div className="form-grupo">
        <div className="campo">
          <label htmlFor="bloqueo-desde">Desde</label>
          <input id="bloqueo-desde" type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} disabled={enviando} required />
          {camposMal['fechaDesde'] !== undefined && <div className="error-campo">{camposMal['fechaDesde']}</div>}
        </div>
        <div className="campo">
          <label htmlFor="bloqueo-hasta">Hasta</label>
          <input id="bloqueo-hasta" type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} disabled={enviando} required />
          {camposMal['fechaHasta'] !== undefined && <div className="error-campo">{camposMal['fechaHasta']}</div>}
        </div>
      </div>
      <div className="campo">
        <label className="fila" style={{ gap: 6, fontWeight: 'normal' }}>
          <input type="checkbox" style={{ width: 'auto' }} checked={conHorario} onChange={(e) => setConHorario(e.target.checked)} disabled={enviando} />
          Limitar a un horario concreto (si no, es todo el día)
        </label>
      </div>
      {conHorario && (
        <div className="form-grupo">
          <div className="campo">
            <label htmlFor="bloqueo-hora-inicio">Hora inicio</label>
            <input id="bloqueo-hora-inicio" type="time" value={horaInicio} onChange={(e) => setHoraInicio(e.target.value)} disabled={enviando} />
          </div>
          <div className="campo">
            <label htmlFor="bloqueo-hora-fin">Hora fin</label>
            <input id="bloqueo-hora-fin" type="time" value={horaFin} onChange={(e) => setHoraFin(e.target.value)} disabled={enviando} />
          </div>
        </div>
      )}
      <div className="campo">
        <label htmlFor="bloqueo-motivo">Motivo</label>
        <input id="bloqueo-motivo" type="text" value={motivo} onChange={(e) => setMotivo(e.target.value)} disabled={enviando} required />
        {camposMal['motivo'] !== undefined && <div className="error-campo">{camposMal['motivo']}</div>}
      </div>
      {error !== null && <ErrorCaja error={error} />}
      <div className="fila-botones">
        <button type="submit" className="principal" disabled={enviando}>
          {enviando ? 'Guardando…' : 'Crear bloqueo'}
        </button>
        <button type="button" onClick={alCancelar} disabled={enviando}>
          Cancelar
        </button>
      </div>
    </form>
  );
}
